import { useState } from 'react';
import { LessonType } from '../../apis/api-client';
import { Alert, Box, Button, Chip, IconButton, Tooltip, Typography } from '../ui';
import { DeleteIcon, EditIcon, LockOutlinedIcon } from '../icons';
import { lessonTypeMeta } from '../courses/contentTypeMeta';
import { PDFViewer, ScormViewer, TextViewer, VideoViewer, WordPPTViewer } from './viewers';
import QuizPlayer from '../quiz/QuizPlayer';
import QuizEditor from '../quiz/QuizEditor';
import LessonCompleteOverlay from './LessonCompleteOverlay';

interface LessonViewerProps {
  lesson: any;
  videoPlayback?: any;
  videoLoading?: boolean;
  canEdit: boolean;
  onEdit: (lesson: any) => void;
  onDelete: (lessonId: number) => void;
  onLessonComplete?: (lessonId: number) => void;
  enrollmentId?: number;
  onQuizPassed?: () => void;
  showLinearProgressNotice?: boolean;
  /** When true, dim the lesson content and show the completion overlay
   *  with a "Next lesson" CTA. Set by the parent after a fresh
   *  completion event, cleared on dismiss or advance. */
  showCompletionOverlay?: boolean;
  /** Provided so the overlay can name the next lesson on its CTA. */
  nextLesson?: { id: number | string; title: string } | null;
  /** Advance to the next lesson — wired by the parent. */
  onAdvanceToNext?: () => void;
  /** Hide the overlay without advancing — the lesson stays selected. */
  onDismissCompletion?: () => void;
}


export default function LessonViewer({
  lesson,
  videoPlayback,
  videoLoading,
  canEdit,
  onEdit,
  onDelete,
  onLessonComplete,
  enrollmentId,
  onQuizPassed,
  showLinearProgressNotice,
  showCompletionOverlay,
  nextLesson,
  onAdvanceToNext,
  onDismissCompletion,
}: LessonViewerProps) {
  const [quizEditMode, setQuizEditMode] = useState(false);

  if (!lesson) return null;

  const handleComplete = () => onLessonComplete?.(lesson.id);
  const meta = lessonTypeMeta(lesson.lessonType);

  const renderLessonContent = () => {
    const { lessonType, fileUrl, textContent } = lesson;

    switch (lessonType) {
      case LessonType.Word:
      case LessonType.PPT:
        return <WordPPTViewer fileUrl={fileUrl} onViewedFor10Seconds={handleComplete} />;

      case LessonType.PDF:
        return <PDFViewer fileUrl={fileUrl} onViewedFor10Seconds={handleComplete} />;

      case LessonType.Video:
        return (
          <VideoViewer
            lesson={lesson}
            playback={videoPlayback}
            lessonTitle={lesson.title}
            loading={videoLoading}
            onVideoEnded={handleComplete}
            requireFullWatch={!canEdit}
          />
        );

      case LessonType.Text:
        // Key by lesson id so navigating from one text lesson to another
        // remounts the viewer — its dwell timer only arms on mount, so
        // without a fresh mount the second text lesson never completes.
        return <TextViewer key={lesson.id} content={textContent} onDwellComplete={handleComplete} />;

      case LessonType.Scorm:
        return (
          <ScormViewer
            scormPackageUrl={lesson.scormPackageUrl}
            scormEntryPoint={lesson.scormEntryPoint}
            onComplete={handleComplete}
          />
        );

      case LessonType.Quiz:
        if (canEdit && quizEditMode) {
          return (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button variant="outlined" color="primary" size="small" onClick={() => setQuizEditMode(false)}>
                  Preview Quiz
                </Button>
              </Box>
              <QuizEditor lessonId={lesson.id} />
            </Box>
          );
        }
        return (
          <Box>
            {canEdit && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 3, pt: 2 }}>
                <Button variant="outlined" color="primary" size="small" onClick={() => setQuizEditMode(true)}>
                  Edit Questions
                </Button>
              </Box>
            )}
            <QuizPlayer
              lessonId={lesson.id}
              enrollmentId={enrollmentId}
              passThreshold={lesson.quizSettings?.passThreshold ?? 80}
              requirePass={lesson.quizSettings?.preventProgression ?? true}
              onQuizPassed={onQuizPassed}
              onComplete={handleComplete}
            />
          </Box>
        );

      default:
        return (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
            Unsupported lesson type: {lessonType}
          </Typography>
        );
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Lesson header — type chip + title + admin actions. */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          {/* Type-coded icon chip — mirrors the sidebar's row icon. */}
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              bgcolor: `${meta.color}15`,
              color: meta.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {meta.icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
              <Chip
                label={meta.label}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  bgcolor: `${meta.color}15`,
                  color: meta.color,
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
              {lesson.title}
            </Typography>
            {lesson.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {lesson.description}
              </Typography>
            )}
          </Box>
          {canEdit && (
            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => onEdit(lesson)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove">
                <IconButton size="small" onClick={() => onDelete(lesson.id)} sx={{ color: 'error.main' }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>

      {/* Lesson content */}
      {renderLessonContent()}

      {/* Linear-progression notice — shown when the course forces lessons
          to be taken in order and this viewer can't bypass. */}
      {showLinearProgressNotice && (
        <Box sx={{ p: 2 }}>
          <Alert severity="info" icon={<LockOutlinedIcon fontSize="inherit" />}>
            Lessons must be completed in order. Finish this lesson to unlock the next one.
          </Alert>
        </Box>
      )}

      {/* Completion overlay — portaled to the viewport (not clipped by
          this viewer's overflow:hidden) and dims the page behind the
          card. Cleared on advance or dismiss by the parent. */}
      {showCompletionOverlay && onAdvanceToNext && onDismissCompletion && (
        <LessonCompleteOverlay
          lessonTitle={lesson.title || 'this lesson'}
          nextLesson={nextLesson ?? null}
          onAdvance={onAdvanceToNext}
          onDismiss={onDismissCompletion}
        />
      )}
    </Box>
  );
}
